import { CalendarCheck, EyeSlash, FunnelSimple, MapTrifold, Plus, Sparkle } from '@phosphor-icons/react';
import { EventCard } from '../../components/EventCard';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { events } from '../../api/endpoints';
import { ScreenError, ScreenLoader, ServerScreen } from '../../components/layout/Screens';
import { EmptyState, SectionTitle } from '../../components/ui/Bits';
import { SearchField } from '../../components/ui/Field';
import { Popover } from '../../components/ui/Popover';
import {
  AUCUN_FILTRE,
  EventFilters,
  filtreActif,
  type EtatLocalisation,
  type Filtres,
} from './EventFilters';
import { distanceKm, type Point } from '../../utils/distance';
import { useAsync, useDebounced } from '../../hooks/useAsync';
import { useCascade } from '../../hooks/useCascade';
import { useTabNav } from '../../components/layout/TabNav';
import { useSession } from '../../store/SessionContext';
import { CLE_DES_SORTIES, sortiesVues } from './sortiesVues';
import { mesLanguesDAbord, seComprennent } from '../../utils/langues';
import { t } from '../../i18n';

/**
 * Onglet "Evenements" : ce qui se passe dans le spot courant.
 *
 * La recherche est retardee : on ne veut pas une requete par frappe. Le
 * filtre par categorie, lui, ne repasse pas par la base — la liste d'une
 * ville tient en memoire, et filtrer cote client repond au doigt.
 */
export function EventsScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);

  /*
    Ensemble vide = aucun filtre, donc tout passe. C'est ce qui evite de
    distinguer "rien de coche" de "tout est coche".

    Les deux bornes de la periode sont au format `AAAA-MM-JJ` comme les
    dates des evenements : deux chaines de cette forme se comparent
    directement, sans passer par un objet Date. L'une sans l'autre a un
    sens — « a partir du 12 », « jusqu'au 20 » — et les deux egales font
    une journee precise.
  */
  const [applique, setApplique] = useState<Filtres>(AUCUN_FILTRE);
  const [filterOpen, setFilterOpen] = useState(false);

  /*
    Ce qu'on regle dans la fenetre, avant de l'appliquer.

    La feuille d'avant filtrait au doigt, ce qui allait : elle couvrait la
    liste, on ne voyait rien bouger. La fenetre, elle, laisse la liste
    visible derriere — et la voir se recomposer a chaque pastille
    touchee, sous une fenetre qui la masque a moitie, donne le vertige.
    D'ou le brouillon, et le bouton qui le valide.
  */
  const [brouillon, setBrouillon] = useState<Filtres>(AUCUN_FILTRE);

  /*
    Ou je suis, pour le filtre de distance.

    Releve a la demande et jamais garde : le point n'est utile que le
    temps de comparer, et le retenir d'une session a l'autre filtrerait
    un jour depuis une ville qu'on a quittee.
  */
  const [moi, setMoi] = useState<Point | null>(null);
  const [localisation, setLocalisation] = useState<EtatLocalisation>(null);

  /*
    On demande des que le brouillon reclame un rayon, et non a
    l'application des filtres : la question de l'autorisation se pose
    alors pendant que la fenetre est ouverte, ou son resultat s'affiche —
    plutot que derriere une fenetre refermee sur une liste inchangee.
  */
  const veutUnRayon = brouillon.rayon !== null || applique.rayon !== null;
  useEffect(() => {
    if (!veutUnRayon || moi) return;
    let vivant = true;
    setLocalisation('attente');
    void events.whereAmI().then((point) => {
      if (!vivant) return;
      setMoi(point);
      setLocalisation(point ? 'trouvee' : 'introuvable');
    });
    return () => {
      vivant = false;
    };
  }, [veutUnRayon, moi]);

  /*
    La carte se prepare pendant qu'on lit la liste.

    Son premier affichage paie tout d'un coup : la bibliotheque d'Apple,
    quelques centaines de kilooctets, et l'aller-retour jusqu'a la
    fonction qui signe son jeton, qui peut demarrer a froid. On est ici a
    un doigt de la carte — autant que l'attente soit passee avant le
    geste. Import dynamique, pour que rien de tout cela n'alourdisse le
    demarrage de l'application.
  */
  useEffect(() => {
    /* Des l'arrivee : c'est la bibliotheque d'Apple et l'aller-retour du
       jeton qui coutent, et ni l'un ni l'autre ne gene la liste. */
    const minuteur = setTimeout(() => {
      void import('./carte/CarteApple').then((module) => module.prechargerLaCarte());
    }, 0);
    return () => clearTimeout(minuteur);
  }, []);

  const filterBtnRef = useRef<HTMLButtonElement>(null);

  /*
    Seule la liste sans recherche est retenue : c'est celle qu'on retrouve
    en revenant sur l'onglet. Garder chaque terme tape reviendrait a
    ressortir de vieux resultats sur une frappe identique.
  */
  const { user, server } = useSession();
  /* Vrai le temps d'un tirage : lui seul fait entrer les sorties nouvelles. */
  const tirage = useRef(false);
  const { data, loading, error, reload, refresh } = useAsync(
    () =>
      debouncedSearch
        ? events.aVenir(debouncedSearch)
        : sortiesVues(tirage.current, user?.id),
    [debouncedSearch],
    debouncedSearch ? undefined : CLE_DES_SORTIES,
  );

  /** Tirer la liste : la seule relecture qui laisse entrer le neuf. */
  async function tirer() {
    tirage.current = true;
    try {
      await refresh();
    } finally {
      tirage.current = false;
    }
  }

  const retenus = (data?.sorties ?? []).filter(
    (event) =>
      (applique.categories.size === 0 || applique.categories.has(event.category)) &&
      (applique.langues.size === 0 || seComprennent(event.langues, [...applique.langues])) &&
      /* Spontanees ou en ligne : l'une ou l'autre nature, pas les deux a la fois. */
      (!(applique.spontanees || applique.enLigne) ||
        (applique.spontanees && event.spontane) ||
        (applique.enLigne && event.enLigne)) &&
      (!applique.from || event.date >= applique.from) &&
      (!applique.to || event.date <= applique.to),
  );

  /*
    Le rayon ne s'applique que si l'on sait d'ou l'on part.

    Sans position, il ne retire rien : filtrer depuis un point invente
    donnerait une liste fausse, et vider la liste sans rien dire est ce
    qui fait croire a une panne. La fenetre porte alors la raison.
  */
  const rayon = applique.rayon;
  const rayonActif = rayon !== null && moi !== null;
  /* En ligne, il n'y a pas de trajet : l'evenement est toujours a portee. */
  const dansLeRayon = rayonActif
    ? retenus.filter(
        (event) =>
          event.enLigne ||
          (event.lat !== null &&
            event.lon !== null &&
            distanceKm(moi, { lat: event.lat, lon: event.lon }) <= rayon),
      )
    : retenus;
  /*
    Les sorties dans mes langues d'abord, les autres ensuite, chaque
    groupe dans l'ordre des dates. On ne cache rien : on remonte ce ou
    l'on se comprendra.
  */
  const filtered = mesLanguesDAbord(dansLeRayon, user?.languesParlees ?? []);

  /*
    Les sorties d'avant qu'on exige une adresse choisie n'ont pas de
    point : on ne peut ni les placer, ni pretendre qu'elles sont proches.
    Elles sortent de la liste, et le nombre est dit — c'est la difference
    entre ecarter et escamoter.
  */
  const sansAdresse = rayonActif
    ? retenus.filter((event) => !event.enLigne && (event.lat === null || event.lon === null)).length
    : 0;

  const filtering = filtreActif(applique);

  /*
    Trois sorties qui me ressemblent, en tete de liste.

    Seulement sur la liste entiere : un filtre ou une recherche disent deja
    ce qu'on cherche, et une suggestion par-dessus n'y repondrait pas.
  */
  /* Une suggestion dans une langue qu'on ne parle pas n'en est pas une. */
  const pourToi =
    filtering || rayonActif || debouncedSearch
      ? []
      : (data?.pourToi ?? []).filter((event) =>
          seComprennent(event.langues, user?.languesParlees ?? event.langues),
        );

  /*
    Les cartes paraissent par vagues, a mesure qu'on descend.

    La cle suit la liste affichee et non son seul nombre : filtrer peut
    changer les sorties sans en changer le compte, et les nouvelles
    doivent paraitre comme les autres.
  */
  const liste = useRef<HTMLDivElement>(null);
  const { drag, backPeek } = useTabNav();
  useCascade(
    'evenements',
    liste,
    filtered.map((event) => event.id).join(),
    /* Voir `TrophiesScreen` : les deux balayages retiennent la vague. */
    !drag && !backPeek,
  );

  /* Ouvrir, c'est reprendre les filtres en cours : la fenetre montre ce
     qui est applique, pas une ardoise vierge. */
  function ouvrirFiltres() {
    setBrouillon({
      ...applique,
      categories: new Set(applique.categories),
      langues: new Set(applique.langues),
    });
    setFilterOpen(true);
  }

  function appliquer() {
    setApplique({
      ...brouillon,
      categories: new Set(brouillon.categories),
      langues: new Set(brouillon.langues),
    });
    setFilterOpen(false);
  }

  /* Effacer vaut pour le brouillon et pour ce qui est applique : un
     bouton « Effacer » qui laisserait la liste filtree ne voudrait rien
     dire. */
  function effacer() {
    setBrouillon(AUCUN_FILTRE());
    setApplique(AUCUN_FILTRE());
    setFilterOpen(false);
  }

  return (
    <ServerScreen
      onRefresh={tirer}
      overlay={
        /*
          La fenetre passe par la fente `overlay` : posee dans le contenu,
          elle serait rognee par la zone defilante et en allongerait la
          hauteur.
        */
        <Popover
          open={filterOpen}
          anchor={filterBtnRef}
          onClose={() => setFilterOpen(false)}
          label={t.sorties.filtres.titre}
        >
          <EventFilters
            valeur={brouillon}
            onChange={setBrouillon}
            onAppliquer={appliquer}
            onEffacer={effacer}
            localisation={brouillon.rayon !== null ? localisation : null}
            campus={server?.campus === true}
            prive={server?.prive === true}
          />
        </Popover>
      }
    >
      <div className="ev-page">
        <div className="ev-search-row">
          <SearchField
            tone="spot"
            placeholder={t.sorties.liste.rechercher}
            className="ev-search-row__field search--croix-du-fil"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
          />
          {/*
            Deux portes, deux signes : la carte des sorties, puis mon
            agenda. Le mot « Mon agenda » prenait la moitie de la ligne a
            la recherche ; l'icone seule suffit a qui l'a vue une fois, et
            libere la place pour la carte.
          */}
          <Link to="/app/events/carte" className="ev-rond" aria-label={t.sorties.liste.carte}>
            <MapTrifold size={20} />
          </Link>
          <Link to="/app/events/agenda" className="ev-rond" aria-label={t.sorties.liste.agenda}>
            <CalendarCheck size={20} />
          </Link>
        </div>

        <div className="ev-tools">
          <Link to="/app/events/new" className="ev-create">
            <Plus size={16} weight="bold" />
            {t.sorties.liste.creer}
          </Link>

          <div className="ev-tools__right">
            {/* La seule porte vers ce qu'on a masque : sans elle, le geste
                etait sans retour. */}
            <Link to="/app/events/hidden" className="ev-filter-btn" aria-label={t.sorties.liste.masques}>
              <EyeSlash size={20} />
            </Link>

            {/* La pastille dit combien de categories filtrent, sans un mot. */}
            <button
              ref={filterBtnRef}
              className="ev-filter-btn"
              aria-label={filtering ? t.sorties.liste.filtreActif : t.sorties.liste.filtrer}
              onClick={ouvrirFiltres}
            >
              {/* Un point, et rien d'autre : l'icone reste la meme. */}
              <FunnelSimple size={20} />
              {filtering && <span className="ev-filter-btn__dot" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {/* Le tourniquet ne parait que s'il n'y a rien a montrer : la
            liste en cache se reaffiche tout de suite, et la relecture se
            fait en silence derriere. */}
        {loading && !data && <ScreenLoader />}
        {error && <ScreenError message={error} onRetry={reload} />}

        {sansAdresse > 0 && (
          <p className="ev-ecartes">
            {sansAdresse === 1
              ? t.sorties.liste.unSansAdresse
              : t.sorties.liste.sansAdresse(sansAdresse)}
          </p>
        )}

        {data && filtered.length === 0 && (
          <EmptyState
            title={t.sorties.liste.aucun}
            hint={
              rayonActif
                ? t.sorties.liste.rienAMoinsDe(rayon)
                : filtering
                  ? t.sorties.liste.elargir
                  : t.sorties.liste.personne
            }
            action={{ label: t.sorties.liste.proposer, onClick: () => navigate('/app/events/new') }}
          />
        )}

        {pourToi.length > 0 && filtered.length > 0 && (
          <section className="ev-pourtoi" aria-label={t.sorties.liste.pourToi}>
            <SectionTitle icon={<Sparkle size={18} />}>{t.sorties.liste.pourToi}</SectionTitle>
            <div className="ev-list">
              {pourToi.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
            <SectionTitle>{t.sorties.liste.toutes}</SectionTitle>
          </section>
        )}

        {filtered.length > 0 && (
          <div className="ev-list" ref={liste}>
            {filtered.map((event) => (
              <EventCard key={event.id} event={event} cascade />
            ))}
          </div>
        )}
      </div>
    </ServerScreen>
  );
}

/**
 * Carte de la liste.
 *
 * L'affiche a gauche, tout le reste a droite. Les quatre reperes tenaient
 * avant sur deux rangees de deux, ce qui obligeait a lire en zigzag et
 * laissait la carte muette : rien ne distinguait une sortie d'une autre
 * avant d'avoir lu son titre. Une image les separe d'un coup d'oeil.
 *
 * Rien n'a change de ce qu'elle dit — ce sont les memes faits, le meme
 * lien, les memes etats. Seule leur disposition change.
 */
